/**
 * Wheel Share Video — preset front-door model (C4, SPEC §5.2).
 *
 * The three one-tap presets that pick a segment set + a target duration for the
 * render, so the common case is a single tap (SPEC §5.2 "Presets (default
 * view)"). Pure, framework-free — no React, no network — so the preset→params
 * mapping and the availability math are exhaustively jest-testable and the modal
 * stays a thin presentational shell (same split as ./shareVideo, ./capabilities).
 *
 * Each preset resolves to render params the POST body carries alongside
 * `aspect_ratio` (see ./shareVideo buildRequestPayload):
 *   - `preset`             — the semantic key, so the BE can resolve the actual
 *                            segment set (hero / first-N / all) its own way.
 *   - `segment_count`      — the max number of Segments to include (omitted for
 *                            Full reveal = "all", so the BE uses every Segment).
 *   - `target_duration_ms` — the desired length (omitted for Full reveal, whose
 *                            length scales with Segment count and is capped
 *                            per-platform by the C3 duration-cap logic, BE-side).
 *
 * BE PARAM GAP (measured 2026-08-15, BE sandbox 1be45097): the generate-share-
 * video[-async] view currently reads ONLY `aspect_ratio` from request.data and
 * ignores extra keys. The service layer (`generate_share_video`) already accepts
 * a `segment_ids` kwarg, but the view never passes it, and there is no duration/
 * preset param. Honoring these fields is a follow-on BE unit — the FE sends them
 * honestly rather than faking a segment set it cannot know from `wheelId` alone.
 */

// Teaser/Quick durations are SPEC §5.2 explicit (~6s / ~12s). Full reveal is
// deliberately null (scales with count, capped by C3) — SPEC ❓10 is the exact
// per-segment seconds + hard cap, still open for Gordon; the FE stays honest and
// lets the BE scale+cap rather than guessing a number here.
export const SHARE_VIDEO_PRESETS = Object.freeze([
  {
    key: "teaser",
    label: "Teaser",
    desc: "1 hero Segment",
    // The most-viewed / first / user-picked Segment (SPEC §5.2). The BE resolves
    // which one; the FE only asks for a single-Segment reveal.
    segmentCount: 1,
    durationSec: 6,
  },
  {
    key: "quick",
    label: "Quick tour",
    desc: "3 Segments",
    segmentCount: 3,
    durationSec: 12,
  },
  {
    key: "full",
    label: "Full reveal",
    desc: "All Segments",
    // null => "all" (no segment_count sent) and "scale + cap" (no duration sent).
    segmentCount: null,
    durationSec: null,
  },
]);

/** Teaser is the default highlighted preset (SPEC §5.4 "Default"). */
export const DEFAULT_PRESET = "teaser";

export const isValidPreset = (key) =>
  SHARE_VIDEO_PRESETS.some((p) => p.key === key);

/** Resolve a preset key to its record; unknown/missing falls back to the default. */
export const resolvePreset = (key) =>
  SHARE_VIDEO_PRESETS.find((p) => p.key === key) ||
  SHARE_VIDEO_PRESETS.find((p) => p.key === DEFAULT_PRESET);

/**
 * The render params a preset contributes to the POST body. `segment_count` and
 * `target_duration_ms` are OMITTED when the preset leaves them null (Full
 * reveal), so the BE treats their absence as "all Segments" / "scale + cap".
 */
export function presetToParams(key) {
  const p = resolvePreset(key);
  const params = { preset: p.key };
  if (p.segmentCount != null) params.segment_count = p.segmentCount;
  if (p.durationSec != null) params.target_duration_ms = p.durationSec * 1000;
  return params;
}

/**
 * Whether a preset can run given the Wheel's Segment count. When the count is
 * unknown (parent didn't supply it) nothing is disabled — the modal only has a
 * `wheelId` today, so it can't know the count without the parent passing it.
 * A preset needs at least as many Segments as it reveals; Full reveal needs ≥1.
 */
export const isPresetAvailable = (key, segmentCount) => {
  if (segmentCount == null) return true;
  const need = resolvePreset(key).segmentCount ?? 1;
  return Number(segmentCount) >= need;
};
